/* import __COLOCATED_TEMPLATE__ from './multilingual.hbs'; */
/* RESPONSIBLE TEAM: team-inbox */

import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { dropTask } from 'ember-concurrency-decorators';
import { taskFor } from 'ember-concurrency-ts';
import type { TaskGenerator } from 'ember-concurrency';
import { post } from 'embercom/lib/ajax';
import type GlossaryService from 'embercom/services/glossary-service';
import { type TabItem } from '../common/standard-base';

interface Args {
  model: any;
  tab?: string;
  changeTab: (tab: string) => void;
}

interface Signature {
  Args: Args;
  Element: never;
}

export default class Multilingual extends Component<Signature> {
  @service declare appService: AppService;
  @service declare glossaryService: GlossaryService;
  @service declare notificationsService: $TSFixMe;
  @service declare intl: $TSFixMe;
  @service declare conversationTranslationSettings: $TSFixMe;
  @service declare session: $TSFixMe;
  @service declare store: $TSFixMe;

  @tracked showImportGlossaryModal = false;
  @tracked showDeleteGlossaryModal = false;
  @tracked isSideDrawerOpen = false;
  @tracked rowData: $TSFixMe;
  @tracked mode: 'new' | 'edit' = 'new';
  @tracked isAiInboxTranslationsEnabled = false;
  @tracked allLanguagesEnabled = false;

  constructor(owner: any, args: Args) {
    super(owner, args);
    this.loadInitialValues();
  }

  async loadInitialValues() {
    try {
      // Load app settings for AI translations and language mode
      await this.session.workspace.appSettingsLoader.promise;
      let settings = await this.session.workspace.appSettingsLoader.value;
      this.isAiInboxTranslationsEnabled = settings?.aiInboxTranslationsEnabled ?? false;
      this.allLanguagesEnabled = settings?.allLanguagesEnabled ?? false;

      // Ensure messenger settings are loaded
      if (!this.messengerSettings && this.app?.id) {
        await this.store.findRecord('messenger-settings/all', this.app.id);
      }
    } catch (error) {
      console.error('Failed to load app settings:', error);
    }
  }

  get isSaving() {
    return taskFor(this.saveMessengerSettings).isRunning;
  }

  @action
  showLearnMore() {
    // Open learn more documentation
    window.Intercom('showArticle', 11098770);
  }

  @action
  addManually() {
    this.openSideDrawer('new');
  }

  @action
  async closeSideDrawer(updatedData?: any) {
    // If data was updated, reload the glossary data
    if (updatedData) {
      await this.glossaryService.loadGlossary();
    }

    this.isSideDrawerOpen = false;
  }

  @action
  openSideDrawer(mode: 'new' | 'edit', rowData?: any) {
    this.isSideDrawerOpen = true;
    this.rowData = rowData;
    this.mode = mode;
  }

  @action
  async closeImportGlossaryModal() {
    this.showImportGlossaryModal = false;
  }

  @action
  deleteGlossary() {
    this.showDeleteGlossaryModal = true;
  }

  @action
  closeDeleteGlossaryModal() {
    this.showDeleteGlossaryModal = false;
  }

  get glossaryExists() {
    return this.glossaryService.exists;
  }

  get selectedTab(): string {
    return this.args.tab || 'general';
  }

  get tabs(): Array<TabItem> {
    return [
      {
        label: 'new-settings.workspace.multilingual.tabs.general',
        value: 'general',
      },
      {
        label: 'new-settings.workspace.multilingual.tabs.glossary',
        value: 'glossary',
      },
    ];
  }

  @action
  changeTab(newTab: string): void {
    this.args.changeTab(newTab);
  }

  // Language settings getters and methods
  get app() {
    return this.appService.app;
  }

  get generalSettings() {
    return this.args.model?.generalSettings;
  }

  get model() {
    return this.args.model;
  }

  get messengerSettings() {
    return this.model?.messengerSettings;
  }

  get languageSettings() {
    // Access the languages relationship from the messenger settings model
    return this.messengerSettings?.languages;
  }

  get supportedLocales() {
    return this.languageSettings?.supportedLocales || [];
  }

  get supportedLocalesWithoutDefault() {
    return this.supportedLocales.filter(
      (locale: any) => locale.localeId !== this.languageSettings?.defaultLocale,
    );
  }

  get localeDropdownItems() {
    return (
      this.supportedLocales.map((locale: any) => ({
        value: locale.localeId,
        text: locale.name,
      })) || []
    );
  }

  get localeDropdownItemsWithoutDefault() {
    return this.supportedLocalesWithoutDefault.map((locale: any) => ({
      value: locale.localeId,
      text: locale.name,
      isSelected: this.localeIsPermitted(locale),
    }));
  }

  get permittedLocales() {
    return this.supportedLocales.filter((locale: any) => this.localeIsPermitted(locale)) || [];
  }

  get defaultLocaleObject() {
    return this.supportedLocales?.find(
      (locale: any) => locale.localeId === this.languageSettings?.defaultLocale,
    );
  }

  get permittedLocalesWithoutDefault() {
    return this.permittedLocales.filter(
      (locale: any) => locale.localeId !== this.languageSettings?.defaultLocale,
    );
  }

  get notPermittedLocales() {
    return this.supportedLocales.filter((locale: any) => !this.localeIsPermitted(locale)) || [];
  }

  get locales() {
    return this.supportedLocales.map((locale: any) => ({
      value: locale.localeId,
      text: locale.name,
      isSelected: this.localeIsPermitted(locale),
    }));
  }

  get showPaywallForLanguages() {
    return !this.app?.hasMultipleLanguages;
  }

  get canAccessLanguageSettings() {
    return true;
    // return this.session.teammate.hasCopilotAccess
  }

  get supportedLanguagesSelectLabel() {
    let languageNames = this.permittedLocalesWithoutDefault.map((locale: any) => locale.name);
    let maxLanguages = this.supportedLocalesWithoutDefault.length;

    switch (languageNames.length) {
      case maxLanguages:
        return this.intl.t('new-settings.workspace.general.languages-section.all');
      case 0:
        return this.intl.t('new-settings.workspace.general.languages-section.none');
      case 1:
        return languageNames[0];
      case 2:
        return this.intl.t('new-settings.workspace.general.languages-section.two-languages', {
          language1: languageNames[0],
          language2: languageNames[1],
        });
      case 3:
        return this.intl.t('new-settings.workspace.general.languages-section.three-languages', {
          language1: languageNames[0],
          language2: languageNames[1],
          language3: languageNames[2],
        });
      default:
        return this.intl.t('new-settings.workspace.general.languages-section.more-languages', {
          language1: languageNames[0],
          language2: languageNames[1],
          count: languageNames.length - 2,
        });
    }
  }

  localeIsPermitted(locale: any) {
    let defaultLocale = this.languageSettings?.defaultLocale;
    return locale.isPermitted || locale.localeId === defaultLocale;
  }

  @action
  setDefaultLanguage(localeId: string) {
    let permitted = this.permittedLocales;

    if (permitted.find((locale: any) => locale.localeId === localeId)) {
      let oldDefaultLocale = this.defaultLocaleObject;
      if (oldDefaultLocale) {
        oldDefaultLocale.isPermitted = true;
      }
    }

    if (this.languageSettings) {
      this.languageSettings.defaultLocale = localeId;

      let newDefaultLocale = this.defaultLocaleObject;
      if (newDefaultLocale) {
        newDefaultLocale.isPermitted = false;
      }
    }
  }

  @action
  removeLanguage(localeId: string) {
    let localeToRemove = this.supportedLocales.find((locale: any) => locale.localeId === localeId);
    if (localeToRemove) {
      localeToRemove.isPermitted = false;
    }
  }

  @action
  permitLanguage(localeId: string) {
    let locale = this.supportedLocales.find((locale: any) => locale.localeId === localeId);
    if (locale) {
      locale.isPermitted = true;
    }
  }

  @action
  handleSelectionChange(newLanguageIds: string[]) {
    if (newLanguageIds.length === this.supportedLocalesWithoutDefault.length) {
      this.setLanguageMode('all');
    } else {
      this.setLanguageMode('specific');
    }

    this.supportedLocalesWithoutDefault.forEach((locale: any) => {
      if (newLanguageIds.includes(locale.localeId)) {
        this.permitLanguage(locale.localeId);
      } else {
        this.removeLanguage(locale.localeId);
      }
    });
  }

  @action
  selectAll() {
    // Select all available language IDs
    this.supportedLocalesWithoutDefault.forEach((locale: any) => {
      this.permitLanguage(locale.localeId);
    });
  }

  @action
  clear() {
    // Only keep the default locale in selection
    this.supportedLocalesWithoutDefault.forEach((locale: any) => {
      if (locale.localeId !== this.languageSettings?.defaultLocale) {
        this.removeLanguage(locale.localeId);
      }
    });
  }

  @action
  setLanguageMode(mode: string) {
    this.allLanguagesEnabled = mode === 'all';
  }

  @action
  async toggler(toggleType: string) {
    if (toggleType === 'ai-inbox-translations') {
      this.isAiInboxTranslationsEnabled = !this.isAiInboxTranslationsEnabled;
    }
  }

  @dropTask
  *saveMessengerSettings(): TaskGenerator<void> {
    try {
      if (!this.app.id || !this.messengerSettings) {
        return;
      }

      yield this.saveAiInboxTranslations();
      yield this.saveAllLanguagesEnabled();
      yield this.messengerSettings.save();

      this.notificationsService.notifyConfirmation(
        this.intl.t('settings.general.save-button.success-notification'),
      );
    } catch (error: any) {
      let notificationText;
      let responseJSON = error.jqXHR?.responseJSON;
      if (responseJSON && responseJSON.errors?.[0]) {
        notificationText = responseJSON.errors[0];
      } else {
        notificationText = this.intl.t('settings.general.save-button.failure-notification');
      }
      this.notificationsService.notifyError(notificationText);
    }
  }

  async saveAiInboxTranslations() {
    await post(`/ember/inbox/app_settings/update_ai_inbox_translations`, {
      app_id: this.app.id,
      ai_inbox_translations_enabled: this.isAiInboxTranslationsEnabled,
    });
    this.session.workspace.aiInboxTranslationsEnabled = this.isAiInboxTranslationsEnabled;
    await this.session.workspace.reloadAppSettings();
  }

  async saveAllLanguagesEnabled() {
    await post(`/ember/inbox/app_settings/update_language_settings`, {
      app_id: this.app.id,
      all_languages_enabled: this.allLanguagesEnabled,
    });
    this.session.workspace.allLanguagesEnabled = this.allLanguagesEnabled;
    await this.session.workspace.reloadAppSettings();
  }

  @action
  save() {
    taskFor(this.saveMessengerSettings).perform();
  }

  @action
  importGlossary() {
    this.showImportGlossaryModal = true;
  }

  @action
  exportGlossary() {
    this.glossaryService.exportGlossary();
  }

  get glossaryOptions() {
    let items = [
      {
        text: this.intl.t('new-settings.workspace.multilingual.import-csv'),
        icon: 'filled-cloud-upload',
        isDestructive: false,
        onSelectItem: () => this.importGlossary(),
      },
    ];

    if (this.glossaryExists) {
      items.push(
        {
          text: this.intl.t('new-settings.workspace.multilingual.export-csv'),
          icon: 'download',
          isDestructive: false,
          onSelectItem: () => this.exportGlossary(),
        },
        {
          text: this.intl.t('new-settings.workspace.multilingual.delete-glossary'),
          icon: 'trash',
          isDestructive: true,
          onSelectItem: () => this.deleteGlossary(),
        },
      );
    }

    return [{ items }];
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'NewSettings::Workspace::Multilingual': typeof Multilingual;
  }
}
